/**
 * Burst tracker — rolling-window bookkeeping for repeated query shapes.
 *
 * Owns the window arithmetic the N+1 detector reasons over: how many times one
 * signature repeated recently, when the burst started, and whether this repeat
 * is worth reporting. Pure and timer-free; callers supply the timestamps.
 */

import { type ObservedQuery, querySignature } from "./query-shape";

/** How much repetition, over how long, counts as a burst. */
export interface BurstWindow {
  /** Repeats of one signature inside the window that count as N+1. */
  readonly threshold: number;
  /** Rolling window in milliseconds. */
  readonly windowMs: number;
}

export interface BurstStatus {
  /** Repeats of this signature inside the window, including this query. */
  readonly count: number;
  readonly firstSeenAt: number;
  /** Whether this repeat crossed a reporting step. */
  readonly shouldReport: boolean;
  readonly signature: string;
}

export interface BurstTracker {
  reset(): void;
  track(query: ObservedQuery): BurstStatus;
}

interface BurstState {
  /** Repeat count already reported for the current burst; 0 when quiet. */
  reportedCount: number;
  /** Timestamps of in-window queries for one signature, oldest first. */
  timestamps: number[];
}

/**
 * Creates a tracker. Reports escalate by doubling (threshold, 2x, 4x, …) so a
 * sustained burst stays visible without flooding logs, and a signature that
 * falls quiet for a full window reports again on its next burst.
 */
export function createBurstTracker(window: BurstWindow): BurstTracker {
  const bursts = new Map<string, BurstState>();

  function oldestAllowedFor(query: ObservedQuery): number {
    return query.startedAt - window.windowMs;
  }

  /** Drops timestamps that aged out, re-arming a burst that fully decayed. */
  function keepInWindow(state: BurstState, query: ObservedQuery): void {
    const oldestAllowed = oldestAllowedFor(query);
    let firstKept = 0;
    while (firstKept < state.timestamps.length) {
      if (state.timestamps[firstKept] >= oldestAllowed) {
        break;
      }
      firstKept += 1;
    }
    if (firstKept > 0) {
      state.timestamps = state.timestamps.slice(firstKept);
    }
    if (state.timestamps.length < window.threshold) {
      // Burst decayed below the threshold: the next crossing is news again.
      state.reportedCount = 0;
    }
  }

  /** Forgets signatures whose bursts fully decayed, bounding the burst map. */
  function dropIdleBursts(query: ObservedQuery, activeSignature: string): void {
    const oldestAllowed = oldestAllowedFor(query);
    for (const [signature, state] of bursts) {
      if (signature === activeSignature) {
        continue;
      }
      if ((state.timestamps.at(-1) ?? 0) < oldestAllowed) {
        bursts.delete(signature);
      }
    }
  }

  function shouldReport(state: BurstState): boolean {
    const count = state.timestamps.length;
    if (count < window.threshold) {
      return false;
    }
    if (state.reportedCount === 0) {
      return true;
    }
    return count >= state.reportedCount * 2;
  }

  return {
    reset() {
      bursts.clear();
    },

    track(query) {
      const signature = querySignature(query);
      const state = bursts.get(signature) ?? {
        reportedCount: 0,
        timestamps: [],
      };
      keepInWindow(state, query);
      state.timestamps.push(query.startedAt);
      bursts.set(signature, state);
      dropIdleBursts(query, signature);

      const reportable = shouldReport(state);
      if (reportable) {
        state.reportedCount = state.timestamps.length;
      }
      return {
        count: state.timestamps.length,
        firstSeenAt: state.timestamps[0],
        shouldReport: reportable,
        signature,
      };
    },
  };
}
